/**
 * @file よむタブ — 読書の面。
 *
 * @what: 段階ごとの候補を、画面の幅に合わせて 1〜3 列で並べ、行の「読んだ」を押した時点で
 *   記録する。記録した直後にひとこと・また読みたい・取り消しを出す。読んだ本には印を出し、
 *   これまで読んだ本を新しい順に並べる。カタログに無い本は書名か ISBN で記録できる。
 *   表紙はタップで拡大し、並べた行の「なおす」で日付・また読みたい・ひとことをあとから直せる。
 * @why: 読書はワーク (教材) と別の枠にする。解くものではないので到達度モデルの単元にせず、
 *   冊数・達成率・残数も出さない。※なぜなら数えると読書が消化すべき量になり、続けること
 *   自体が目的である読み聞かせの位置づけと矛盾するためである。
 *   「読んだ」を押しても下のフォームに入るだけだと反応が無いように見えるので、押した時点で
 *   記録する (代表 2026-08-28)。ひとことは記録のあとから足せる。
 * @failure: 候補一覧の取得失敗は 1 行で知らせる (backend 未デプロイの間は 404 になる)。
 *   記録の追加・削除の失敗も 1 行で、入力は消さない。あとからの追記 (また読みたい・ひとこと) が
 *   失敗したときは画面の入力だけが変わってサーバには届かないので、確認の枠を閉じずに残す
 *   (同じ枠でもう一度触れば送り直せる)。詳細は console のみ。
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { formatLogDate, todayLocal } from "../api/dates";
import { newProgressLogId, type StudyProgressLog, type StudyProgressLogInput } from "../api/plans";
import { latestCatalogs, levelsOf, type StudyReadingBook, type StudyReadingCatalog } from "../api/readingCatalogs";
import { lookupBook, parseIsbn } from "../api/bookLookup";
import type { ProgressLogsState } from "../hooks/useProgressLogs";
import type { ReadingCatalogsState } from "../hooks/useReadingCatalogs";
import {
  bookKey,
  catalogBookKey,
  parseReadingLog,
  serializeReadingLog,
  type ReadingLogBook,
} from "../model/readingLog";
import { levelAudience, levelOrderHint } from "../model/readingLevels";
import { READING_REALM_ID } from "../model/realms";
import { LogEditForm } from "./LogEditForm";
import { MaterialCover } from "./MaterialsPanel";
import "./study.css";

/**
 * 面を開いたときに自動で読み進める記録の上限。
 *
 * @why: 既読の印はその学習者の記録から導くので、記録を読み進めないと印が付かない。一方で
 *   1 ページ 30件なので、際限なく読むと面を開くたびに何十回もの取得が走る。毎日 1 件残して
 *   半年ぶんにあたる 180件までを自動で読み、それより古い記録は利用者が求めたときだけ読む。
 */
const AUTO_SCAN_LIMIT = 180;

/**
 * 「古い記録も読む」で読み進める上限。
 *
 * @why: 毎日 1 件残して約 3年分。ここに達したら古い記録は印に反映されないので、黙って
 *   打ち切らずに画面で知らせる (印が付いていない本を未読と誤読させない)。
 */
const MANUAL_SCAN_LIMIT = 1000;

/** これまで読んだ本の一覧に出す件数。 */
const RECENT_LIMIT = 30;

/** 直前に記録した 1 件 (ひとこと・また読みたい・取り消しの対象)。 */
interface JustRecorded {
  logId: string;
  date: string;
  book: ReadingLogBook;
}

export function ReadingPanel({
  familyId,
  planReady,
  hasLearners,
  catalogsState,
  logsState,
  onAdd,
  onRemove,
  onLoadMore,
  busy,
  actionError,
}: {
  /** 直しの枠が使う家族 id (読書の記録に写真は付かないが、共通の枠が受け取る)。 */
  familyId: string;
  planReady: boolean;
  hasLearners: boolean;
  catalogsState: ReadingCatalogsState;
  logsState: ProgressLogsState;
  onAdd: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  onRemove: (logId: string) => void;
  onLoadMore: () => void;
  busy: boolean;
  actionError: string | null;
}) {
  const [date, setDate] = useState(todayLocal());
  const [justRecorded, setJustRecorded] = useState<JustRecorded | null>(null);
  const [again, setAgain] = useState(false);
  const [memo, setMemo] = useState("");
  const [manualTitle, setManualTitle] = useState("");
  const [manualIsbn, setManualIsbn] = useState("");
  const [isbnInput, setIsbnInput] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [levelId, setLevelId] = useState<string | null>(null);
  // 直している最中の記録 (1 行だけ開く)
  const [editingId, setEditingId] = useState<string | null>(null);
  // 連打よけ。親の busy は再描画を待つので、同じ描画の中で 2 回押されると 2 件できる。
  const recording = useRef(false);

  const logs = useMemo(() => (logsState.status === "ready" ? logsState.items : []), [logsState]);
  const nextCursor = logsState.status === "ready" ? logsState.nextCursor : undefined;

  // 既読の印はその学習者の記録から導くので、面を開いたら半年ぶんまでは自動で読み進める。
  // realm での絞り込みはサーバに置けない (realm は暗号化ペイロードの中にあり Firestore の
  // クエリが書けない) ので、記録そのものを読むしかない。
  useEffect(() => {
    if (busy || !nextCursor || logs.length >= AUTO_SCAN_LIMIT) return;
    onLoadMore();
  }, [busy, nextCursor, logs.length, onLoadMore]);

  const readingLogs = useMemo(
    () =>
      logs
        .map((log) => ({ log, detail: parseReadingLog(log) }))
        .filter((x): x is { log: StudyProgressLog; detail: NonNullable<typeof x.detail> } => x.detail !== null),
    [logs],
  );

  // 読んだ本の集合と、最新の記録の「また読みたい」。記録が真実で、状態 doc は持たない。
  const readKeys = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const { detail } of readingLogs) {
      const key = bookKey(detail.book);
      // 記録は新しい順なので、最初に出たものが最新
      if (key && !map.has(key)) map.set(key, detail.again === true);
    }
    return map;
  }, [readingLogs]);

  const catalogs = catalogsState.status === "ready" ? latestCatalogs(catalogsState.catalogs) : [];
  const catalog: StudyReadingCatalog | undefined = catalogs[0];
  const levels = useMemo(() => (catalog ? levelsOf(catalog) : []), [catalog]);

  // 開く段階は「最後に記録した本の段階」。無ければ最初の段階。※なぜなら進み方は子が決めるもので、
  // 月齢や年齢から段階を決めないためである。
  const lastRecordedLevel = readingLogs.find((x) => x.detail.book.catalog_id === catalog?.id)?.detail.book.level;
  const activeLevel =
    levelId ?? (lastRecordedLevel && levels.includes(lastRecordedLevel) ? lastRecordedLevel : levels[0]);
  const booksOfLevel = catalog ? catalog.books.filter((b) => b.level === activeLevel) : [];

  if (!planReady) {
    return hasLearners ? <p className="study-muted">お子さんを選ぶと読んだ本の記録が出ます。</p> : null;
  }

  /** 1 冊を記録する。押した時点で残し、ひとことは後から足せるようにする。 */
  const record = async (book: ReadingLogBook): Promise<boolean> => {
    if (recording.current) return false;
    recording.current = true;
    try {
      const logId = newProgressLogId();
      const created = await onAdd({
        id: logId,
        date,
        realm: READING_REALM_ID,
        detail: serializeReadingLog(book, false),
      });
      if (!created) return false;
      setJustRecorded({ logId, date, book });
      setAgain(false);
      setMemo("");
      setLookupError(null);
      return true;
    } finally {
      recording.current = false;
    }
  };

  const recordFromCatalog = (b: StudyReadingBook) => {
    const book: ReadingLogBook = { title: b.title };
    if (catalog) {
      book.catalog_id = catalog.id;
      book.level = b.level;
      book.no = b.no;
    }
    if (b.isbn) book.isbn = b.isbn;
    void record(book);
  };

  const recordManual = async () => {
    const title = manualTitle.trim();
    if (!title) return;
    const book: ReadingLogBook = { title };
    if (manualIsbn) book.isbn = manualIsbn;
    if (await record(book)) {
      setManualTitle("");
      setManualIsbn("");
      setIsbnInput("");
    }
  };

  /** 直前の記録に、また読みたい・ひとことを足す (同じ id への再送 = 上書き)。 */
  const amend = async (nextAgain: boolean, nextMemo: string) => {
    if (!justRecorded) return;
    const input: StudyProgressLogInput = {
      id: justRecorded.logId,
      date: justRecorded.date,
      realm: READING_REALM_ID,
      detail: serializeReadingLog(justRecorded.book, nextAgain),
    };
    if (nextMemo.trim()) input.memo = nextMemo.trim();
    await onAdd(input);
  };

  const undo = () => {
    if (!justRecorded) return;
    onRemove(justRecorded.logId);
    setJustRecorded(null);
    setAgain(false);
    setMemo("");
  };

  const lookupManual = async () => {
    const isbn13 = parseIsbn(isbnInput);
    if (!isbn13) {
      setLookupError("ISBN か本の商品ページの URL を入れてください。");
      return;
    }
    setLookingUp(true);
    setLookupError(null);
    try {
      const meta = await lookupBook(isbn13);
      if (!meta) {
        setLookupError("この ISBN の本が見つかりませんでした。書名を手で入れてください。");
        return;
      }
      setManualTitle(meta.title);
      setManualIsbn(meta.isbn);
    } catch (err) {
      console.error("[study] failed to look up a book", err);
      setLookupError("書名を引けませんでした。時間を置くか、書名を手で入れてください。");
    } finally {
      setLookingUp(false);
    }
  };

  // 自動で読む分を読み終えても、まだ古い記録が残っている状態。
  const hasOlderLogs = nextCursor !== undefined && logs.length >= AUTO_SCAN_LIMIT;
  const canReadOlder = hasOlderLogs && logs.length < MANUAL_SCAN_LIMIT;
  const audience = catalog && activeLevel ? levelAudience(catalog.series_id, activeLevel) : null;
  const orderHint = catalog ? levelOrderHint(catalog.series_id, levels) : null;

  return (
    <>
      <section className="study-section">
        <h2 className="study-section-title">
          <span aria-hidden="true">📕</span> 読んだ本を残す
        </h2>
        <p className="study-section-help">
          下の一覧で「読んだ」を押すと、その場で記録します。一覧に無い本は書名か ISBN で足せます。
        </p>
        <div className="study-log-form">
          <label className="study-log-field">
            <span>日付</span>
            <input
              type="date"
              value={date}
              max={todayLocal()}
              onChange={(e) => setDate(e.target.value || todayLocal())}
            />
          </label>
          <label className="study-log-field study-log-field--memo">
            <span>一覧に無い本</span>
            <input
              type="text"
              placeholder="書名を入れる"
              maxLength={200}
              value={manualTitle}
              onChange={(e) => setManualTitle(e.target.value)}
            />
          </label>
          <label className="study-log-field">
            <span>ISBN</span>
            <input
              type="text"
              placeholder="ISBN か商品ページの URL"
              value={isbnInput}
              onChange={(e) => setIsbnInput(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="study-ghost-btn"
            onClick={() => void lookupManual()}
            disabled={lookingUp || !isbnInput.trim()}
          >
            {lookingUp ? "引いています…" : "書名を引く"}
          </button>
          <button
            type="button"
            className="study-primary-btn"
            onClick={() => void recordManual()}
            disabled={busy || !manualTitle.trim() || logsState.status !== "ready"}
          >
            この本を記録する
          </button>
        </div>
        {justRecorded ? (
          <div className="study-reading-just">
            <p>
              「{justRecorded.book.title}」を記録しました。
              <button type="button" className="study-link-btn" disabled={busy} onClick={undo}>
                取り消す
              </button>
            </p>
            <label className="study-reading-again">
              <input
                type="checkbox"
                checked={again}
                disabled={busy}
                onChange={(e) => {
                  setAgain(e.target.checked);
                  void amend(e.target.checked, memo);
                }}
              />
              <span>また読みたがった</span>
            </label>
            <label className="study-log-field study-log-field--memo">
              <span>ひとこと</span>
              <input
                type="text"
                placeholder="例: へびの絵で毎回笑っていた"
                maxLength={500}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                onBlur={() => void amend(again, memo)}
              />
            </label>
          </div>
        ) : null}
        {lookupError || actionError ? (
          <p role="alert" className="study-error">
            {lookupError ?? actionError}
          </p>
        ) : null}
      </section>

      <section className="study-section">
        <h2 className="study-section-title">
          <span aria-hidden="true">📚</span> 読む本をさがす
        </h2>
        {catalogsState.status === "loading" ? (
          <p role="status" className="study-muted">
            候補一覧を読み込んでいます...
          </p>
        ) : catalogsState.status === "error" ? (
          <p role="alert" className="study-error">
            候補一覧を読み込めませんでした。時間を置いて再読み込みしてください。
          </p>
        ) : !catalog ? (
          <p className="study-muted">
            まだ候補一覧を取り込んでいません。取り込むと、段階ごとの本がここに並びます。
          </p>
        ) : (
          <>
            <p className="study-section-help">
              {catalog.title}
              {catalog.edition ? `（${catalog.edition}）` : ""} 出典 {catalog.source_name}
              {catalog.source_url ? (
                <>
                  {" "}
                  <a href={catalog.source_url} target="_blank" rel="noreferrer">
                    原本を見る
                  </a>
                </>
              ) : null}
            </p>
            {levels.length > 1 ? (
              <div className="study-reading-levels" role="tablist" aria-label="段階">
                {levels.map((lv) => {
                  const hint = levelAudience(catalog.series_id, lv);
                  return (
                    <button
                      key={lv}
                      type="button"
                      role="tab"
                      aria-selected={lv === activeLevel}
                      className="study-reading-level-tab"
                      onClick={() => setLevelId(lv)}
                    >
                      <span className="study-reading-level-id">{lv}</span>
                      {hint ? <span className="study-reading-level-hint">{hint}</span> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {audience || orderHint ? (
              <p className="study-section-help">
                {audience ? `${activeLevel} は${audience}が目安です。` : ""}
                {orderHint ?? ""}
              </p>
            ) : null}
            {hasOlderLogs ? (
              <p className="study-muted">
                読んだ本が多いため、古いものには印が付かないことがあります。
                {canReadOlder ? (
                  <>
                    {" "}
                    <button type="button" className="study-link-btn" disabled={busy} onClick={onLoadMore}>
                      古い記録も読む
                    </button>
                  </>
                ) : null}
              </p>
            ) : null}
            <ul className="study-reading-books" aria-label="候補の本">
              {booksOfLevel.map((b) => {
                const key = catalog ? catalogBookKey(catalog.id, b.level, b.no) : "";
                const read = readKeys.has(key);
                return (
                  <li
                    key={key}
                    className={read ? "study-reading-book study-reading-book--read" : "study-reading-book"}
                  >
                    <MaterialCover isbn={b.isbn} title={b.title} enlargeable />
                    <div className="study-reading-book-main">
                      <div className="study-reading-book-title">
                        {read ? <span aria-label="読んだ本">🌸</span> : null} {b.title}
                        {readKeys.get(key) ? <span title="また読みたがった"> ♡</span> : null}
                      </div>
                      {b.author ? <div className="study-reading-book-credit">{b.author}</div> : null}
                      {b.publisher || b.note ? (
                        <div className="study-reading-book-meta">
                          {b.publisher ? (
                            <span className="study-reading-book-publisher">{b.publisher}</span>
                          ) : null}
                          {b.note ? <span className="study-reading-book-note">{b.note}</span> : null}
                        </div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="study-ghost-btn"
                      disabled={busy}
                      onClick={() => recordFromCatalog(b)}
                      aria-label={`${b.title} を読んだ`}
                    >
                      読んだ
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {readingLogs.length > 0 ? (
        <section className="study-section">
          <h2 className="study-section-title">
            <span aria-hidden="true">🕒</span> これまで読んだ本
          </h2>
          <ul className="study-reading-log-list" aria-label="これまで読んだ本">
            {readingLogs.slice(0, RECENT_LIMIT).map(({ log, detail }) =>
              editingId === log.id ? (
                <li key={log.id} className="study-reading-log-editing">
                  <p className="study-reading-log-editing-title">{detail.book.title}</p>
                  <LogEditForm
                    log={log}
                    familyId={familyId}
                    kind="reading"
                    materials={[]}
                    busy={busy}
                    onSave={onAdd}
                    onClose={() => setEditingId(null)}
                  />
                </li>
              ) : (
                <li key={log.id}>
                  <span className="study-reading-log-date">{formatLogDate(log.date, false)}</span>
                  <span className="study-reading-log-body">
                    {detail.book.title}
                    {detail.again ? <span title="また読みたがった"> ♡</span> : null}
                    {log.memo ? <span className="study-muted"> {log.memo}</span> : null}
                  </span>
                  <span className="study-log-row-actions">
                    <button
                      type="button"
                      className="study-link-btn"
                      disabled={busy}
                      onClick={() => setEditingId(log.id)}
                      aria-label={`${detail.book.title} の記録をなおす`}
                    >
                      なおす
                    </button>
                    <button
                      type="button"
                      className="study-link-btn"
                      disabled={busy}
                      onClick={() => onRemove(log.id)}
                      aria-label={`${detail.book.title} の記録を消す`}
                    >
                      消す
                    </button>
                  </span>
                </li>
              ),
            )}
          </ul>
        </section>
      ) : null}
    </>
  );
}
